import { useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSWRConfig } from 'swr';

import { ConfirmationDialog, useActionBarContribution, type ActionBarAction } from '~components';
import FormDialog from '~components/dialogs/formDialog';
import VectorImage from '~components/media/vectorImage';
import { pagePathSegments } from '~constants';
import { StatusVariant } from '~enums';
import { useSession } from '~hooks';
import ApiError from '~services/apiError';
import { getService } from '~services/serviceFactory';

import EditUserForm, { EditUserFormResults } from '../forms/editUserForm';
import UsersService from '../services/usersService';

export type UserActionBarActions = 'edit' | 'delete';

interface UserActionsBarProps {
	userId: string;
	organisationId?: string | undefined | null;
	allowRoleEditing: boolean;
	allowDeleting: boolean;
}
const usersService = getService(UsersService);

const UserActionsBar = ({
	userId,
	organisationId,
	allowRoleEditing,
	allowDeleting = false,
}: UserActionsBarProps) => {
	const { t } = useTranslation();
	const { user, updateSessionUser } = useSession();
	const navigate = useNavigate();
	const location = useLocation();

	const { enqueueSnackbar } = useSnackbar();
	const { mutate } = useSWRConfig();

	const [userAction, setUserAction] = useState<UserActionBarActions | null>(null);
	const [showModalLoader, setShowModalLoader] = useState<boolean>(false);

	const clearUserAction = () => {
		setUserAction(null);
	};

	const updateUser = async (formData: EditUserFormResults) => {
		setShowModalLoader(true);
		await usersService
			.updateUser({
				userId: userId,
				userData: formData,
				organisationId: organisationId,
			})
			.then((updatedUser) => {
				// current user will have to trigger mutate on /me
				const cacheKey = usersService.getUserByIdCacheKey(
					userId === user?.id ? 'me' : (userId ?? ''),
					organisationId ?? undefined
				);

				// Mutate swr with updated user, so the inforcard is updated as well.
				mutate(cacheKey, updatedUser, false);

				if (updatedUser.id === user?.id) {
					updateSessionUser(updatedUser);
				}

				setShowModalLoader(false);
				enqueueSnackbar(t('user.messages.updateUserSuccess', { ns: 'entities' }), {
					variant: 'success',
				});
			})
			.catch(() => {
				enqueueSnackbar(t('user.messages.updateUserFailure', { ns: 'entities' }), {
					variant: 'error',
				});
			})
			.finally(() => {
				clearUserAction();
				setShowModalLoader(false);
			});
	};

	const confirmDelete = async () => {
		setShowModalLoader(true);
		await usersService
			.deleteUser(userId, organisationId)
			.then(() => {
				enqueueSnackbar(t('userDeletedSuccesfully', { ns: 'toast' }), { variant: 'success' });
				// If we came from the organisations page, we simply go back, otherwise we default to the user list
				const from: string | undefined = location.state?.from;
				if (from?.includes(pagePathSegments.Organisations)) {
					navigate(-1);
				} else {
					navigate(`/${pagePathSegments.Users}`, { replace: true });
				}
			})
			.catch((error: ApiError) => {
				enqueueSnackbar(error.getDescription(), { variant: 'error' });
			})
			.finally(() => {
				setShowModalLoader(false);
			});
	};

	const actions = useMemo<ActionBarAction[]>(() => {
		const list: ActionBarAction[] = [
			{
				id: `user-${userId}-edit`,
				label: t('edit', { ns: 'actions' }),
				icon: <VectorImage name='icon/edit' />,
				onClick: () => setUserAction('edit'),
			},
		];

		if (allowDeleting) {
			list.push({
				id: `user-${userId}-delete`,
				label: t('delete', { ns: 'actions' }),
				icon: <VectorImage name='icon/bin' />,
				variant: StatusVariant.Destructive,
				onClick: () => setUserAction('delete'),
			});
		}

		return list;
	}, [t, userId, allowDeleting]);

	useActionBarContribution(actions, { id: `user-actions-${userId}` });

	return (
		<>
			<FormDialog
				open={userAction === 'edit'}
				title={t('user.fields.editUser', { ns: 'entities' })}
				loading={showModalLoader}
			>
				<EditUserForm
					userId={userId}
					organisationId={organisationId ?? undefined}
					allowRoleEditing={allowRoleEditing}
					onSubmit={async (formData) => updateUser(formData)}
					onCancel={clearUserAction}
				/>
			</FormDialog>
			<ConfirmationDialog
				open={userAction === 'delete'}
				title={t('user.labels.deleteUserDialogTitle', { ns: 'entities' })}
				helperText={t('user.labels.deleteUserDialogDescription', { ns: 'entities' })}
				confirmButtonText={t('delete', { ns: 'actions' })}
				variant={StatusVariant.Destructive}
				icon={<VectorImage name='icon/bin' />}
				onConfirm={confirmDelete}
				onClose={clearUserAction}
				loading={showModalLoader}
			/>
		</>
	);
};

export default UserActionsBar;
